import React, { useState, useMemo, useCallback } from "react";
import clsx from "clsx";
import { useKeyPressEvent } from "../hooks";
import { stripPunctuation } from "../utils/strings";

interface FuzzySearchItem {
  id: number;
  name: string;
}

interface Props {
  items: FuzzySearchItem[];
  onItemSelect: (id: number) => void;
  noMatchingResultsMessage?: string;
}

const FuzzySearch: React.FC<Props> = ({
  items,
  onItemSelect,
  noMatchingResultsMessage = "No matching results",
}) => {
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = e.target.value.toLowerCase();
    setQuery(searchQuery);
    e.stopPropagation();
  };

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        stripPunctuation(item.name.toLowerCase()).includes(
          stripPunctuation(query.toLowerCase())
        )
      ),
    [JSON.stringify(items), query]
  );

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if ((event.key === "j" || event.key === "n") && event.ctrlKey) {
        setFocusedIndex((prevIndex) =>
          Math.min(filteredItems.length - 1, prevIndex + 1)
        );
      } else if ((event.key === "k" || event.key === "p") && event.ctrlKey) {
        setFocusedIndex((prevIndex) => Math.max(0, prevIndex - 1));
      } else if (event.key === "Enter") {
        onItemSelect(filteredItems[focusedIndex].id);
      }
    },
    [filteredItems, focusedIndex]
  );

  useKeyPressEvent(
    {
      id: "fuzzy-search",
      priority: 1,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    true
  );

  return (
    <div>
      <input
        type="text"
        autoFocus
        value={query}
        onChange={handleSearch}
        placeholder="Search..."
        className="w-full px-2 py-1 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline"
      />
      <div className="py-2">
        <div className="divide-y divide-gray-200">
          {filteredItems.length === 0 && <div>{noMatchingResultsMessage}</div>}
          {filteredItems.map((item, itemIndex) => (
            <div key={item.id}>
              <button
                className={clsx({ "bg-gray-200": itemIndex === focusedIndex })}
                onClick={() => onItemSelect(item.id)}
              >
                {item.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FuzzySearch;
